import React, { useState, useEffect } from 'react';
import { useReveal } from '../hooks/useScrollReveal';
import { cn } from '../../../lib/utils';

const testimonials = [
  {
    id: 1,
    quote: "Neura replaced three tools and saves our team 15+ hours a week. The calendar automation alone is worth it.",
    author: "Sarah Chen",
    role: "Designer at Figma",
    avatar: "https://images.unsplash.com/photo-1701615004837-40d8573b6652?q=80&w=1480&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    metric: "15 hrs saved/wk",
  },
  {
    id: 2,
    quote: "I was skeptical about AI in my inbox. After one week with Neura, I can't imagine going back.",
    author: "Marcus Johnson",
    role: "Engineer at Vercel",
    avatar: "https://plus.unsplash.com/premium_photo-1671656349218-5218444643d8?q=80&w=1287&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    metric: "80% less email time",
  },
  {
    id: 3,
    quote: "The attention to detail is unmatched. Standups went from 30 minutes to a 2-minute daily digest.",
    author: "Elena Rodriguez",
    role: "Founder at Craft",
    avatar: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    metric: "Meetings cut 60%",
  },
];

const LandingTestimonials: React.FC = () => {
  const ref = useReveal();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayedQuote, setDisplayedQuote] = useState(testimonials[0].quote);
  const [displayedRole, setDisplayedRole] = useState(testimonials[0].role);
  const [displayedMetric, setDisplayedMetric] = useState(testimonials[0].metric);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleSelect = (index: number) => {
    if (index === activeIndex || isAnimating) return;
    setIsAnimating(true);

    setTimeout(() => {
      setDisplayedQuote(testimonials[index].quote);
      setDisplayedRole(testimonials[index].role);
      setDisplayedMetric(testimonials[index].metric);
      setActiveIndex(index);
      setTimeout(() => setIsAnimating(false), 400);
    }, 200);
  };

  useEffect(() => {
    // If user is hovering over any avatar, pause auto-rotation
    if (hoveredIndex !== null) return;

    const timer = setInterval(() => {
      const nextIndex = (activeIndex + 1) % testimonials.length;
      handleSelect(nextIndex);
    }, 1500);

    return () => clearInterval(timer);
  }, [activeIndex, hoveredIndex, isAnimating]);

  return (
    <section id="testimonials" className="relative py-10 md:py-14 bg-[#fafafa] overflow-hidden grain-overlay scroll-mt-20">
      <div className="section-line" />
      
      <div ref={ref} className="max-w-7xl mx-auto px-6 pt-2 flex flex-col items-center">
        {/* Section Header */}
        <div className="max-w-2xl text-center mb-16 sr">
          <p className="text-[11px] font-semibold text-black/30 tracking-[0.2em] uppercase mb-4">Testimonials</p>
          <h2 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.04em] text-black leading-[1.1] mb-4">
            Loved by people<br />who ship fast
          </h2>
        </div>

        {/* Testimonials Core interactive component */}
        <div className="w-full max-w-3xl bg-white border border-black/[0.05] rounded-3xl p-8 md:p-12 shadow-[0_2px_40px_-12px_rgba(0,0,0,0.06)] relative sr delay-2">
          {/* Subtle Ambient Glow inside card */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50/20 to-emerald-50/10 rounded-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center gap-8">
            {/* Metric Badge */}
            <div className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100/80 transition-all duration-500 ease-out",
              isAnimating ? "opacity-0 translate-y-1" : "opacity-100 translate-y-0"
            )}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-[10px] font-semibold text-emerald-600/70 uppercase tracking-wider">{displayedMetric}</span>
            </div>

            {/* Quote Container */}
            <div className="relative px-4 md:px-8 w-full min-h-[140px] flex items-center justify-center">
              <span className="absolute -left-2 -top-6 text-7xl font-serif text-black/[0.04] select-none pointer-events-none">
                "
              </span>

              <p
                className={cn(
                  "text-xl md:text-[26px] font-light text-black text-center max-w-2xl leading-relaxed transition-all duration-400 ease-out",
                  isAnimating ? "opacity-0 blur-sm scale-[0.98]" : "opacity-100 blur-0 scale-100",
                )}
              >
                {displayedQuote}
              </p>

              <span className="absolute -right-2 -bottom-8 text-7xl font-serif text-black/[0.04] select-none pointer-events-none">
                "
              </span>
            </div>

            <div className="flex flex-col items-center gap-5 mt-2">
              {/* Role text */}
              <p
                className={cn(
                  "text-[11px] text-black/40 tracking-[0.2em] font-semibold uppercase transition-all duration-500 ease-out",
                  isAnimating ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0",
                )}
              >
                {displayedRole}
              </p>

              {/* Selector buttons */}
              <div className="flex items-center justify-center gap-3">
                {testimonials.map((testimonial, index) => {
                  const isActive = activeIndex === index;
                  const isHovered = hoveredIndex === index && !isActive;
                  const showName = isActive || isHovered;

                  return (
                    <button
                      key={testimonial.id}
                      onClick={() => handleSelect(index)}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className={cn(
                        "relative flex items-center gap-0 rounded-full cursor-pointer",
                        "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
                        isActive ? "bg-black text-white shadow-md" : "bg-transparent hover:bg-black/[0.04]",
                        showName ? "pr-4 pl-2 py-1.5" : "p-0.5",
                      )}
                    >
                      {/* Avatar with smooth ring animation */}
                      <div className="relative flex-shrink-0">
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.author}
                          className={cn(
                            "w-8 h-8 rounded-full object-cover",
                            "transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
                            isActive ? "ring-2 ring-white/30" : "ring-0",
                            !isActive && "hover:scale-105",
                          )}
                        />
                      </div>

                      <div
                        className={cn(
                          "grid transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
                          showName ? "grid-cols-[1fr] opacity-100 ml-2" : "grid-cols-[0fr] opacity-0 ml-0",
                        )}
                      >
                        <div className="overflow-hidden">
                          <span
                            className={cn(
                              "text-xs font-semibold whitespace-nowrap block",
                              "transition-colors duration-300",
                              isActive ? "text-white" : "text-black/60",
                            )}
                          >
                            {testimonial.author}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingTestimonials;
